import React, { useState, useMemo, useEffect } from 'react';
import { RiArrowDropDownLine, RiArrowDropUpLine } from "react-icons/ri";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const MAX_DESCRIPTION_LENGTH = 100;

const renderStars = (rating) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
  const stars = [];

  for (let i = 0; i < fullStars; i++) stars.push(<FaStar key={`full-${i}-${Math.random()}`} />);
  if (halfStar) stars.push(<FaStarHalfAlt key={`half-${Math.random()}`} />);
  for (let i = 0; i < emptyStars; i++) stars.push(<FaRegStar key={`empty-${Math.random()}`} />);

  return stars;
};

const ReviewItem = ({ review, isActive }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (!isActive) {
      setIsExpanded(false); 
    }
  }, [isActive]);

  const description = review.description || '';
  const isLongDescription = description.length > MAX_DESCRIPTION_LENGTH;

  return (
    <div className="review-slide-content">
      <div className="review-meta">
        <span className="review-author">Customer</span>
        <span className="review-date">
          {review.createdAt
            ? new Date(review.createdAt).toLocaleDateString()
            : ''}
        </span>
      </div>
      <div className="review-stars">
        {renderStars(review.rating)}
      </div>
      {review.title && <h4 className="review-title">{review.title}</h4>}
      <p className="review-text">
        {isLongDescription && !isExpanded
          ? `${description.substring(0, MAX_DESCRIPTION_LENGTH)}...`
          : description}
        {isLongDescription && (
          <button onClick={toggleExpand} className="see-more-button">
            {isExpanded ? 'See less' : 'See more'}
          </button>
        )}
      </p>
    </div>
  );
};

const ReviewsAccordion = ({ reviews = [], loading = false, onRateProductClick, hasPurchased }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const { averageRating, ratingBreakdown, totalReviews } = useMemo(() => {
    if (!Array.isArray(reviews) || reviews.length === 0) {
      return { averageRating: 0, ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }, totalReviews: 0 };
    }

    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let totalRating = 0;

    reviews.forEach(review => {
      const rating = Math.round(review.rating);
      if (rating >= 1 && rating <= 5) {
        breakdown[rating]++;
        totalRating += review.rating;
      }
    });

    const avg = reviews.length > 0 ? totalRating / reviews.length : 0;

    return {
      averageRating: avg,
      ratingBreakdown: breakdown,
      totalReviews: reviews.length
    };
  }, [reviews]);

  if (loading) {
    return (
      <div className="reviews-accordion-item">
        <div className="reviews-accordion-header">
          <b className={"fontclass"}>Reviews</b>
          <span className="arrow fontclass">
            <RiArrowDropDownLine />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="reviews-accordion-item">
      <div className="reviews-accordion-header" onClick={toggleAccordion}>
        <b className={"fontclass"}>Reviews ({totalReviews})</b>
        <span className="arrow fontclass">
          {isOpen ? <RiArrowDropUpLine /> : <RiArrowDropDownLine />}
        </span>
      </div>

      {isOpen && (
        <div className="reviews-accordion-content fontclass">
          {totalReviews === 0 ? (
            <div className="no-reviews-section">
              <p>Be the first to review this product!</p>
              {hasPurchased && (
                <button className="rate-product-button" onClick={onRateProductClick}>
                  Rate Product
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="reviews-summary">
                <div className="average-rating-section">
                  <div className="average-rating-value">
                    {averageRating.toFixed(1)}
                    <span className="out-of"> / 5</span>
                  </div>
                  <div className="average-rating-stars">
                    {renderStars(averageRating)}
                  </div>
                  <div className="total-ratings-count">
                    {totalReviews} Ratings
                  </div>
                </div>

                <div className="rating-breakdown-section">
                  {Object.keys(ratingBreakdown).sort((a, b) => b - a).map(star => {
                    const count = ratingBreakdown[star];
                    const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                    return (
                      <div className="rating-breakdown-row" key={star}>
                        <span className="star-label">{star} star</span>
                        <div className="rating-bar-container">
                          <div
                            className="rating-bar"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="rating-count">{count}</span>
                      </div>
                    );
                  })}
                </div>

                {hasPurchased && (
                  <button className="rate-product-button" onClick={onRateProductClick}>
                    Rate Product
                  </button>
                )}
              </div>

              <div className="individual-reviews-swiper">
                <Swiper
                  modules={[Pagination, Navigation]}
                  spaceBetween={15}
                  slidesPerView={1}
                  pagination={{ clickable: true }}
                  navigation={true}
                  loop={reviews.length > 1}
                  onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                >
                  {reviews.filter(review => review && typeof review.rating === 'number').map((review, index) => (
                    <SwiperSlide key={review._id || `review-${index}`}>
                      <ReviewItem review={review} isActive={index === activeIndex}/>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewsAccordion;